import { clamp, type ReadonlyRef, type RefOrGetter } from '@/helpers'
import {
    addCoordinate,
    type Coordinate,
    createEmptyCoordinate,
    divideCoordinate,
    type DragCallbackEvent,
    type DragPincher,
    useDragPincher,
} from '@/composables/motion/drag-pincher.ts'
import { computed, type ComputedRef, ref, type Ref, toValue, watch } from 'vue'
import {
    type CssTransitionDto,
    type CssTransitionEasing,
    getCssTransition,
} from '@/composables/motion/css-transition.ts'
import {
    type MouseDrag,
    useMouseDrag,
} from '@/composables/motion/mouse-drag.ts'
import {
    useWheelDirection,
    type WheelDirection,
} from '@/composables/motion/wheel-direction.ts'
import {
    type Clicker,
    useDoubleClicker,
} from '@/composables/motion/double-clicker.ts'
import { getResizeObserverConstructor, getTimeout } from '@/helpers/globals.ts'

interface NiceGuyZoomer {
    // Container width/height
    sizes: ReadonlyRef<Sizes>

    // Position State
    angle: ReadonlyRef<number>
    scale: ReadonlyRef<number>
    translate: ReadonlyRef<Coordinate>
    swipe: ReadonlyRef<Coordinate>

    // Action State
    isRotated: ComputedRef<boolean>
    isZoomed: ComputedRef<boolean>
    isTranslated: ComputedRef<boolean>
    hasClick: ComputedRef<boolean>
    isSwiping: ReadonlyRef<boolean>
    isPinching: ReadonlyRef<boolean>

    // Styles
    isTransitioning: ComputedRef<boolean>
    transformStyle: ComputedRef<string | undefined>
    transitionStyle: ComputedRef<string | undefined>
    cssTransitions: ComputedRef<CssTransitionDto[]>
    style: ComputedRef<string | undefined>

    // TODO Implement cancelTransition
    // Actions
    setScale: (scale: number, transition?: number) => Promise<void>
    center: (transition?: number) => Promise<void>
    reset: (transition?: number) => Promise<void>

    // Dependencies
    drag: MouseDrag
    wheel: WheelDirection
    pincher: DragPincher
    clicker: Clicker

    // Whether all dependencies have bound their event listeners
    // And also that width/height have been obtained from the container
    bound: ComputedRef<boolean>
}

interface NiceGuyZoomerOptions {
    container: RefOrGetter<HTMLElement | null>
    initialScale?: number
    initialAngle?: number
    minScale?: number
    maxScale?: number
    sensitivity?: number
    animateScale?: boolean
    animateTranslate?: boolean
    animateRotate?: boolean
    resetAngleOnDrop?: boolean
    transition?: CssTransitionEasing
    doubleClickDelay?: number
    onSwipe?: (swipe: Coordinate, swiping: boolean) => void
    onClick?: () => void
    onFirstClick?: () => void
    onDoubleClick?: () => void
    disabled?: RefOrGetter<boolean>
}

type Sizes = {
    width: number
    height: number
}

export function useNiceGuyZoomer(options: NiceGuyZoomerOptions): NiceGuyZoomer {
    const withDefaults: Required<NiceGuyZoomerOptions> = {
        initialScale: 1,
        initialAngle: 0,
        minScale: 1,
        maxScale: 2,
        sensitivity: 100,
        animateScale: true,
        animateTranslate: true,
        animateRotate: false,
        resetAngleOnDrop: true,
        disabled: () => false,
        transition: 'ease-in-out',
        doubleClickDelay: 300,
        async onSwipe() {},
        onClick: () => {},
        onFirstClick: () => {},
        onDoubleClick: onDoubleClickDefault,
        ...options,
    }

    const {
        initialScale,
        initialAngle,
        minScale,
        maxScale,
        sensitivity,
        animateScale,
        animateTranslate,
        animateRotate,
        resetAngleOnDrop,
        transition: transitionFunction,
        disabled,
        doubleClickDelay,
        onSwipe,
        onClick,
        onFirstClick,
        onDoubleClick,
    } = withDefaults

    async function onDoubleClickDefault(): Promise<void> {
        if (toValue(disabled)) {
            return
        }

        const scaleDistance = maxScale - minScale
        const midScale = scaleDistance / 2 + minScale

        if (scale.value < 1) {
            await setScale(1)
        } else if (scale.value < midScale) {
            await setScale(midScale)
        } else {
            await setScale(1)
        }
    }

    const sizes: Ref<Readonly<Sizes>> = ref({
        width: 0,
        height: 0,
    })

    const angle: Ref<number> = ref(initialAngle)
    const scale = ref(initialScale)
    const translate: Ref<Readonly<Coordinate>> = ref(createEmptyCoordinate())

    const lastAngle = ref(angle.value)
    const lastScale = ref(scale.value)
    const lastTranslate: Ref<Readonly<Coordinate>> = ref(
        createEmptyCoordinate()
    )

    const swipe: Ref<Readonly<Coordinate>> = ref(createEmptyCoordinate())
    const isSwiping = ref(false)
    const isPinching = ref(false)

    const transition: Ref<number> = ref(0)
    const isTransitioning = computed((): boolean => transition.value > 0)

    function reset(transition?: number): Promise<void> {
        const promise = setTransition(transition)

        angle.value = initialAngle
        scale.value = initialScale
        translate.value = createEmptyCoordinate()

        lastAngle.value = initialAngle
        lastScale.value = initialScale
        lastTranslate.value = createEmptyCoordinate()

        return promise
    }

    const transformStyle = computed(() => {
        const transforms = []

        if (animateScale && scale.value !== 1) {
            transforms.push(`scale3d(${scale.value}, ${scale.value}, 1)`)
        }

        if (animateTranslate) {
            const { x, y } = translate.value

            if (x || y) {
                transforms.push(`translate3d(${x}px, ${y}px, 0px)`)
            }
        }

        if (animateRotate) {
            if (angle.value) {
                transforms.push(`rotate(${angle.value}deg)`)
            }
        }

        if (transforms.length) {
            return `transform: ${transforms.join(' ')}`
        }

        // todo we might not need this
        if (transition.value) {
            return 'transform: scale3d(1, 1, 1) translate3d(0px, 0px, 0px) rotate(0deg)'
        }

        return undefined
    })

    const cssTransitions = computed((): CssTransitionDto[] => {
        const transitions: CssTransitionDto[] = []

        if (transition.value) {
            transitions.push({
                property: 'transform',
                time: transition.value,
                easing: transitionFunction,
            })
        }

        return transitions
    })

    const transitionStyle = computed(() => {
        return getCssTransition(cssTransitions.value)
    })

    const style = computed(() => {
        const styles = []

        if (transformStyle.value) {
            styles.push(transformStyle.value)
        }

        if (transitionStyle.value) {
            styles.push(transitionStyle.value)
        }

        if (styles.length) {
            return styles.join(';')
        }

        return undefined
    })

    const timeout = getTimeout()

    let transitionTimeout: number = 0

    function setTransition(time: number | undefined): Promise<void> {
        if (!timeout || !time) {
            return Promise.resolve()
        }

        return new Promise((resolve) => {
            const { setTimeout, clearTimeout } = timeout

            if (transitionTimeout) {
                clearTimeout(transitionTimeout)
            }

            transition.value = time

            if (time) {
                transitionTimeout = setTimeout(() => {
                    transition.value = 0
                    resolve()
                }, time)
            }
        })
    }

    async function setScale(
        newScale: number,
        transition: number = 250
    ): Promise<void> {
        const promise = setTransition(transition)

        const clampedScale = clamp(newScale, minScale, maxScale)
        scale.value = clampedScale
        lastScale.value = clampedScale

        translate.value = clampTranslate(
            translate.value,
            clampedScale,
            sizes.value
        )
        lastTranslate.value = translate.value

        await promise
    }

    function center(transition: number = 250): Promise<void> {
        const promise = setTransition(transition)

        translate.value = clampTranslate(
            createEmptyCoordinate(),
            scale.value,
            sizes.value
        )
        lastTranslate.value = translate.value

        return promise
    }

    function onDrag(_: HTMLElement, drag: DragCallbackEvent) {
        if (toValue(disabled)) {
            return
        }

        const { dragging, delta } = drag

        const currentScale = scale.value

        if (currentScale === 1) {
            swipe.value = delta
            isSwiping.value = true
            onSwipe(delta, dragging)
        } else {
            translate.value = clampTranslate(
                addCoordinate(
                    lastTranslate.value,
                    divideCoordinate(delta, currentScale)
                ),
                currentScale,
                sizes.value
            )
        }

        if (!dragging) {
            lastTranslate.value = translate.value

            if (swipe.value.x || swipe.value.y || isSwiping.value) {
                swipe.value = createEmptyCoordinate()
                isSwiping.value = false
            }
        }
    }

    const drag = useMouseDrag({
        container: options.container,
        callback: onDrag,
    })

    const wheel = useWheelDirection({
        container: options.container,
        async callback(_, direction) {
            const newScale = getScaleForScroll(scale.value, direction, 1.1)
            await setScale(newScale, 250)
        },
    })

    const pincher = useDragPincher({
        container: options.container,
        onTouchMove(e) {
            if (scale.value !== 1 || e.touches.length > 1) {
                e.preventDefault()
            }
        },
        onDrag,
        onPinch(_, pinch) {
            if (toValue(disabled)) {
                return
            }

            const { pinching, delta } = pinch

            angle.value = lastAngle.value + delta.angle

            const totalDistance = lastScale.value * sensitivity + delta.distance
            const newScale = totalDistance / sensitivity

            const scaleClamped = clamp(newScale, minScale, maxScale)

            scale.value = scaleClamped

            translate.value = clampTranslate(
                addCoordinate(
                    lastTranslate.value,
                    divideCoordinate(delta.drag, scaleClamped)
                ),
                scaleClamped,
                sizes.value
            )

            if (!pinching) {
                lastScale.value = scale.value
                lastTranslate.value = translate.value

                if (resetAngleOnDrop) {
                    setTransition(200)
                    angle.value = 0
                }
                lastAngle.value = angle.value
            }

            isPinching.value = pinching
        },
    })

    const clicker = useDoubleClicker({
        container: options.container,
        onClick,
        onFirstClick,
        onDoubleClick,
        delay: doubleClickDelay,
    })
    const { hasClick } = clicker

    const isZoomed = computed((): boolean => {
        return scale.value !== 1
    })

    const isRotated = computed((): boolean => {
        return angle.value !== 0
    })

    const isTranslated = computed((): boolean => {
        return translate.value.x !== 0 && translate.value.y !== 0
    })

    const bound = computed(() => {
        return (
            drag.bound.value &&
            wheel.bound.value &&
            pincher.bound.value &&
            clicker.bound.value &&
            sizes.value.width > 0 &&
            sizes.value.height > 0
        )
    })

    watch(sizes, () => {
        setScale(scale.value, 0)
    })

    const resizeObserver = getResizeObserverConstructor()

    if (resizeObserver) {
        const observer = new resizeObserver(
            (entries: ResizeObserverEntry[]) => {
                const container = entries[0]

                if (!container) {
                    return
                }

                const { clientWidth, clientHeight } = container.target

                if (clientWidth && clientHeight) {
                    sizes.value = {
                        width: clientWidth,
                        height: clientHeight,
                    }
                }
            }
        )

        watch(options.container, (container, oldContainer) => {
            if (container) {
                const { clientWidth, clientHeight } = container

                if (clientWidth && clientHeight) {
                    sizes.value = {
                        width: clientWidth,
                        height: clientHeight,
                    }
                }

                observer.observe(container)
            } else {
                sizes.value = {
                    width: 0,
                    height: 0,
                }
            }

            if (oldContainer) {
                observer.unobserve(oldContainer)
            }
        })
    }

    return {
        // Container width/height
        sizes,

        // Position State
        angle,
        scale,
        translate,
        swipe,

        // Action State
        isRotated,
        isZoomed,
        isTranslated,
        hasClick,
        isSwiping,
        isPinching,

        // Styles
        isTransitioning,
        transformStyle,
        transitionStyle,
        cssTransitions,
        style,

        // Actions
        setScale,
        center,
        reset,

        // Dependencies
        drag,
        wheel,
        pincher,
        clicker,

        // Whether all dependencies have bound their event listeners
        // And also that width/height have been obtained from the container
        bound,
    }
}

function getScaleForScroll(
    currentScale: number,
    direction: 'up' | 'down',
    factor: number
): number {
    if ('up' === direction) {
        return currentScale * factor
    }

    return currentScale / factor
}

function clampTranslate(
    translate: Coordinate,
    scale: number,
    sizes: Sizes
): Coordinate {
    return {
        x: clampTranslatePoint(translate.x, scale, sizes.width),
        y: clampTranslatePoint(translate.y, scale, sizes.height),
    }
}

export function clampTranslatePoint(
    translate: number,
    scale: number,
    dimension: number
): number {
    const piece = 1 / scale
    const ratio = piece * (scale - 1)
    const max = (ratio * dimension) / 2
    const min = 0 !== max ? max * -1 : 0

    if (scale < 1) {
        return clamp(translate, max, min)
    }

    return clamp(translate, min, max)
}

if (import.meta.vitest) {
    const { describe, test, expect } = import.meta.vitest

    describe('clampTranslatePoint', () => {
        test('0.25 scale', () => {
            expect(clampTranslatePoint(5_000, 0.25, 348)).toBe(522)
            expect(clampTranslatePoint(123, 0.25, 348)).toBe(123)
            expect(clampTranslatePoint(-5_000, 0.25, 348)).toBe(-522)
            expect(clampTranslatePoint(-123, 0.25, 348)).toBe(-123)
        })
        test('1 scale', () => {
            expect(clampTranslatePoint(5_000, 1, 348)).toBe(0)
            expect(clampTranslatePoint(123, 1, 348)).toBe(0)
            expect(clampTranslatePoint(-5_000, 1, 348)).toBe(0)
            expect(clampTranslatePoint(-123, 1, 348)).toBe(0)
        })
        test('2 scale', () => {
            expect(clampTranslatePoint(5_000, 2, 348)).toBe(87)
            expect(clampTranslatePoint(55, 2, 348)).toBe(55)
            expect(clampTranslatePoint(-5_000, 2, 348)).toBe(-87)
            expect(clampTranslatePoint(-55, 2, 348)).toBe(-55)
        })
        test('3 scale', () => {
            expect(clampTranslatePoint(5_000, 3, 348)).toBe(116)
            expect(clampTranslatePoint(55, 3, 348)).toBe(55)
            expect(clampTranslatePoint(-5_000, 3, 348)).toBe(-116)
            expect(clampTranslatePoint(-55, 3, 348)).toBe(-55)
        })
        test('50 scale', () => {
            expect(clampTranslatePoint(5_000, 50, 348)).toBe(170.52)
            expect(clampTranslatePoint(55, 50, 348)).toBe(55)
            expect(clampTranslatePoint(-5_000, 50, 348)).toBe(-170.52)
            expect(clampTranslatePoint(-55, 50, 348)).toBe(-55)
        })
        test('100 scale', () => {
            expect(clampTranslatePoint(5_000, 100, 348)).toBe(172.26)
            expect(clampTranslatePoint(55, 100, 348)).toBe(55)
            expect(clampTranslatePoint(-5_000, 100, 348)).toBe(-172.26)
            expect(clampTranslatePoint(-55, 100, 348)).toBe(-55)
        })
    })
}
